import axios, { AxiosInstance } from 'axios';
import { IUser } from './interfaces/IUser';
import Vue from 'vue';
import { BvModal } from 'bootstrap-vue';

const NETWORK_ERROR_CODE = 100000;

const errorCodeErrorMapping: {
    [key: number]: {
        message: string;
        displayRestError: boolean;
    };
} = {
    [NETWORK_ERROR_CODE]: {
        message: 'Si è verificato un errore generico di rete, riprovare più tardi.',
        displayRestError: false 
    },
    0: {
        message: 'Errore non gestito, contattare l\'assistenza.',
        displayRestError: false
    },
    100: {
        message: 'Si è verificato un errore tecnico, siete pregati di contattare l\'assistenza tecnica.',
        displayRestError: false
    },
    101: {
        message: 'Si è verificata una problematica relativa ai permessi dell\'applicativo, siete pregati di contattare l\'assistenza tecnica.',
        displayRestError: false
    },
    102: {
        message: 'Si è verificato un errore tecnico, siete pregati di contattare l\'assistenza tecnica.',
        displayRestError: false
    },
    103: {
        message: '',
        displayRestError: true
    },
    1001: {
        message: 'Nome utente / password errati.',
        displayRestError: false
    },
    1002: {
        message: 'Si è verificato un errore di validazione.',
        displayRestError: true
    },
    1010: {
        message: 'Attenzione: l\'elemento cercato non esiste.',
        displayRestError: false
    }
};

export class ApiClient {
    public http: AxiosInstance;
    public $bvModalReference!: BvModal;
    private baseURL!: string;
    protected token: string;

    constructor(url: string) {
        this.http = axios.create({
            baseURL: url,
            headers: { 'Content-Type': 'application/json' }
        });
        this.baseURL = url;
        this.token = '';
        this.retrieveToken();
    }

    public setBvModalReference(v: BvModal) {
        this.$bvModalReference = v;
    }

    public get isLoggedIn(): boolean {
        return this.token !== null && this.token !== undefined && this.token !== '';
    }

    public async login(username: string, password: string): Promise<boolean> {
        const loading = this.showLoading();
        let ok = false;
        try {
            const res: IUser = await this.http.post('login', {
                username, password
            });
            console.info('-> res is', res);
            if (res) {
                this.token = this.getResponseData(res as any).token;
                console.info('-> token is ', this.token);
                this.storeToken();
                ok = true;
            }
        }
        catch (e) {
            ok = false;
            this.handleSvcError(e);
        }
        loading.hide();
        return ok;
    }

    private storeToken() {
        sessionStorage.setItem('X-AUTH-TOKEN', this.token);
    }

    private retrieveToken() {
        this.token = sessionStorage.getItem('X-AUTH-TOKEN') || '';
    }

    private getResponseData(obj: { data: any }) {
        return obj.data;
    }

    private showLoading() {
        const loader = Vue.$loading.show({
            container: null,
            canCancel: false
        });
        return loader;
    }

    public async downloadCdrFile(fileName: string): Promise<void> {
        window.open(this.baseURL + 'cdrFileGet/' + fileName);
    }

    public async genericCall(method: 'get'|'post', relativeUrl: string, payload: any) {
        const loading = this.showLoading();
        let res: any = [];
        try {
            let response;
            if (method === 'post') {
                response = await this.http.post(relativeUrl, payload, {
                    headers: this.buildHeaders()
                });
            }
            else if (method === 'get') {
                response = await this.http.get(relativeUrl, {
                    headers: this.buildHeaders()
                });
            }
            else {
                throw `Il metodo ${method} non è supportato.`;
            }
            res = this.getResponseData(response as any);
        }
        catch (e) {
            console.error(e);
            this.handleSvcError(e);
        }
        loading.hide();
        return res;
    }

    public async entityList<ITrackableEntity>(entityName: string, filters?: any): Promise<ITrackableEntity[]> {
        const loading = this.showLoading();
        const relativeUrl = entityName;
        let res: ITrackableEntity[] = [];
        try {
            const response = await this.http.post(relativeUrl, {
                filtri: filters
            }, {
                headers: this.buildHeaders()
            }) as ITrackableEntity[];
            res = this.getResponseData(response as any);
        }
        catch (e) {
            console.error(e);
            this.handleSvcError(e);
        }
        loading.hide();
        return res;
    }

    private buildHeaders() {
        return {
            'X-AUTH-TOKEN': this.token
        }
    }

    public async entityGet<ITrackableEntity>(entityName: string, entityKey: number): Promise<ITrackableEntity | null> {
        const loading = this.showLoading();
        const relativeUrl = `${entityName}/get/${entityKey}`;
        let res: ITrackableEntity | null = null;
        try {
            const response = await this.http.get(relativeUrl, {
                headers: this.buildHeaders()
            }) as ITrackableEntity;
            res = this.getResponseData(response as any);
        }
        catch (e) {
            console.error(e);
            this.handleSvcError(e);
        }
        loading.hide();
        return res;
    }

    public async entityUpdate<ITrackableEntity>(entityName: string, entityRecord: ITrackableEntity): Promise<boolean> {
        const loading = this.showLoading();
        let result = false;
        const relativeUrl = `${entityName}/update`;
        try {
            const response = await this.http.post(relativeUrl, {
                [entityName]: entityRecord
            }, {
                headers: this.buildHeaders()
            });
            result = response.status === 200;
        }
        catch (e) {
            console.error(e);
            this.handleSvcError(e);
        }
        loading.hide();
        return result;
    }

    public async entitySave<ITrackableEntity>(entityName: string, entityRecord: ITrackableEntity): Promise<boolean> {
        const loading = this.showLoading();
        let result = false;
        const relativeUrl = `${entityName}/save`;
        try {
            const response = await this.http.post(relativeUrl, {
                [entityName]: entityRecord
            }, {
                headers: this.buildHeaders()
            });
            result = response.status === 200;
        }
        catch (e) {
            console.error(e);
            this.handleSvcError(e);
        }
        loading.hide();
        return result;
    }

    public async entityDelete<ITrackableEntity>(entityName: string, entityId: number): Promise<boolean> {
        const loading = this.showLoading();
        let result = false;
        const relativeUrl = `${entityName}/delete/${entityId}`;
        try {
            const response = await this.http.post(relativeUrl, {
                headers: this.buildHeaders()
            });
            result = response.status === 200;
        }
        catch (e) {
            console.error(e);
            this.handleSvcError(e);
        }
        loading.hide();
        return result;
    }

    private handleSvcError(restException: any): Promise<void> {
        let response;
        if (!restException || !restException.response || !restException.response.data) {
            response = {
                errorCode: NETWORK_ERROR_CODE,
                errorDetail: void 0
            };
        }
        else response = restException.response.data;
        return this.displayErrorMessage(response.errorCode, response.errorDetail);
    }

    private async displayErrorMessage(errorCode: number, restErrorMessage: string) {
        const errorLookup = errorCodeErrorMapping[errorCode] || errorCodeErrorMapping[0];
        const errorMessage = errorLookup.displayRestError ? [errorLookup.message, restErrorMessage].join('\n') : errorLookup.message;
        return await this.$bvModalReference.msgBoxOk(errorMessage, {
            title: 'Errore'
        });
    }

    public async displayAlert(title: string, message: string) {
        return await this.$bvModalReference.msgBoxOk(message, {
            title
        });
    }

    public async displayConfirm(title: string, message: string) {
        return await this.$bvModalReference.msgBoxConfirm(message, {
            title,
            okTitle: 'Confermo',
            cancelTitle: 'No, annulla'
        });
    }
}

const apiClient = new ApiClient(process.env.VUE_APP_REST_ENDPOINT_BASE_URL);

export default apiClient;